import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AvatarStage, AvatarStrip } from "@/components/AvatarStage";
import { Send, Loader2, Sparkles, Mic, Square, Volume2, VolumeX, Radio, PhoneOff, Play, Share2 } from "lucide-react";
import { CoachShareDialog } from "@/components/CoachShareDialog";

const COACH_AVATAR = "https://static.prod-images.emergentagent.com/jobs/1a9e1561-ca18-4dbb-8f52-13e836a03554/images/063325646653cf8a151ee440b3e604a7efe09ddef4648003466f156b1d0b378f.jpeg";
const COACH_VOICE = "coach";

const SUGGESTIONS = [
  "How do I handle 'it's too expensive'?",
  "I freeze up on cold calls. Help.",
  "How do I build rapport fast?",
  "Someone said they'll 'think about it'. What now?",
];

function RichText({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith("**") && p.endsWith("**")
    ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
    : <React.Fragment key={i}>{p.replace(/(^|\s)\*([^*]+)\*(?=\s|$|[.,!?])/g, "$1$2")}</React.Fragment>);
}

export default function Coach() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [input, setInput] = useState(searchParams.get("q") || "");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState(() => localStorage.getItem("pc_lang") || "en");
  useEffect(() => { localStorage.setItem("pc_lang", language); }, [language]);
  useEffect(() => { api.get("/languages").then((r) => setLanguages(r.data)).catch((err) => console.warn("[Coach] request failed", err)); }, []);
  const currentLang = languages.find((l) => l.id === language);
  const [coachFrames, setCoachFrames] = useState([]);
  useEffect(() => { api.get("/avatars/coach").then((r) => setCoachFrames(r.data.frames || [])).catch((err) => console.warn("[Coach] request failed", err)); }, []);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [level, setLevel] = useState(0);
  const [handsFree, setHandsFree] = useState(false);
  const [hfState, setHfState] = useState("idle"); // idle | listening | thinking | speaking
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const meterCtxRef = useRef(null);
  const meterRafRef = useRef(null);
  const handsFreeRef = useRef(false);
  const hfStreamRef = useRef(null);
  const spokeRef = useRef(false);
  const rafRef = useRef(null);
  const vadCtxRef = useRef(null);

  useEffect(() => () => {
    endHandsFree();
    if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
    audioRef.current?.pause();
    try { meterCtxRef.current?.close(); } catch (err) { console.warn("[Coach] ignored error", err); }
  }, []); // eslint-disable-line

  // ---- Hands-free continuous conversation ----
  const beginHandsFree = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      hfStreamRef.current = stream;
      handsFreeRef.current = true;
      setHandsFree(true);
      setVoiceOn(true);
      listenTurn();
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const endHandsFree = () => {
    handsFreeRef.current = false;
    setHandsFree(false);
    setHfState("idle");
    audioRef.current?.pause();
    setSpeaking(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { mediaRef.current?.state === "recording" && mediaRef.current.stop(); } catch (err) { console.warn("[Coach] ignored error", err); }
    hfStreamRef.current?.getTracks().forEach((t) => t.stop());
    hfStreamRef.current = null;
    try { vadCtxRef.current?.close(); } catch (err) { console.warn("[Coach] ignored error", err); }
    vadCtxRef.current = null;
  };

  const listenTurn = () => {
    if (!handsFreeRef.current || !hfStreamRef.current) return;
    const stream = hfStreamRef.current;
    const mr = new MediaRecorder(stream);
    const chunks = [];
    spokeRef.current = false;
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = async () => {
      if (!handsFreeRef.current) return;
      if (!spokeRef.current) { listenTurn(); return; }
      setHfState("thinking");
      try {
        const fd = new FormData();
        fd.append("audio", new Blob(chunks, { type: "audio/webm" }), "speech.webm");
        fd.append("language", language);
        const { data } = await api.post("/voice/transcribe", fd);
        const text = (data.text || "").trim();
        if (text) {
          const reply = await send(text, false);
          if (reply && handsFreeRef.current) {
            setHfState("speaking");
            const barged = await playTTS(reply, { interruptible: true });
            if (barged) toast("Go ahead — I'm listening.", { icon: "👂" });
          }
        }
      } catch (err) { console.warn("[Coach] ignored error", err); }
      if (handsFreeRef.current) listenTurn();
    };
    mr.start();
    mediaRef.current = mr;
    setHfState("listening");
    monitorSilence(stream, mr);
  };

  const monitorSilence = (stream, mr) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    vadCtxRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const start = Date.now();
    const THRESH = 12, SILENCE_MS = 1400, MAX_MS = 30000, MIN_SPEAK_MS = 400;
    let silenceStart = null, speakStart = null;
    const stop = () => { try { mr.stop(); } catch (err) { console.warn("[Coach] ignored error", err); } try { ctx.close(); } catch (err) { console.warn("[Coach] ignored error", err); } };
    const tick = () => {
      if (!handsFreeRef.current || mr.state !== "recording") return;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = buf[i] - 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();
      if (rms > THRESH) {
        if (!speakStart) speakStart = now;
        if (now - speakStart > MIN_SPEAK_MS) spokeRef.current = true;
        silenceStart = null;
      } else if (spokeRef.current) {
        if (!silenceStart) silenceStart = now;
        else if (now - silenceStart > SILENCE_MS) return stop();
      }
      if (now - start > MAX_MS) return stop();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const meterAudio = (a) => {
    try {
      const ctx = meterCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      meterCtxRef.current = ctx;
      ctx.resume?.();
      const src = ctx.createMediaElementSource(a);
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      src.connect(an);
      an.connect(ctx.destination);
      const buf = new Uint8Array(an.frequencyBinCount);
      let frame = 0;
      const tick = () => {
        if (a.paused || a.ended) { setLevel(0); return; }
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        if (frame++ % 2 === 0) setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3.5));
        meterRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) { console.warn("[Coach] ignored error", err); }
  };

  // While Linden speaks, watch the mic: sustained user speech cuts him off.
  const watchBargeIn = (onBarge) => {
    const stream = hfStreamRef.current;
    if (!stream || !handsFreeRef.current) return () => {};
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const BARGE_THRESH = 22, HOLD_MS = 350, GRACE_MS = 600;
    const start = Date.now();
    let loudSince = null, raf = null, active = true;
    const tick = () => {
      if (!active || !handsFreeRef.current) return;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = buf[i] - 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();
      if (now - start > GRACE_MS) {
        if (rms > BARGE_THRESH) {
          if (!loudSince) loudSince = now;
          else if (now - loudSince > HOLD_MS) { active = false; try { ctx.close(); } catch (err) { console.warn("[Coach] ignored error", err); } onBarge(); return; }
        } else loudSince = null;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { active = false; if (raf) cancelAnimationFrame(raf); try { ctx.close(); } catch (err) { console.warn("[Coach] ignored error", err); } };
  };

  const [replaying, setReplaying] = useState(null);
  const replay = async (i, text) => {
    if (replaying === i) { audioRef.current?.pause(); setSpeaking(false); setLevel(0); setReplaying(null); return; }
    setReplaying(i);
    try { await playTTS(text); } finally { setReplaying(null); }
  };

  const pendingAudioRef = useRef(null);
  const playTTS = (text, { interruptible = false } = {}) => new Promise((resolve) => {
    (async () => {
      let stopWatch = () => {};
      try {
        const pre = pendingAudioRef.current;
        let b64;
        if (pre && pre.text === text) { pendingAudioRef.current = null; b64 = pre.b64; }
        else { const { data } = await api.post("/voice/tts", { text: text.replace(/[*_#`>]+/g, ""), voice: COACH_VOICE, language }); b64 = data.audio_base64; }
        if (!b64) return resolve(false);
        audioRef.current?.pause();
        if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
        const a = new Audio(`data:audio/mp3;base64,${b64}`);
        audioRef.current = a;
        let settled = false;
        const done = (barged = false) => {
          if (settled) return;
          settled = true;
          stopWatch();
          setSpeaking(false); setLevel(0); resolve(barged);
        };
        a.onended = () => done(false);
        a.onerror = () => done(false);
        setSpeaking(true);
        meterAudio(a);
        if (interruptible) stopWatch = watchBargeIn(() => { a.pause(); done(true); });
        a.play().catch(() => done(false));
      } catch { stopWatch(); setSpeaking(false); resolve(false); }
    })();
  });

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        await sendAudio(new Blob(chunksRef.current, { type: "audio/webm" }));
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setVoiceOn(true);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };

  const sendAudio = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "speech.webm");
      fd.append("language", language);
      const { data } = await api.post("/voice/transcribe", fd);
      const text = (data.text || "").trim();
      if (!text) { toast.error("Didn't catch that — try again."); return; }
      await send(text, true);
    } catch {
      toast.error("Couldn't transcribe your voice.");
    } finally {
      setTranscribing(false);
    }
  };

  useEffect(() => { if (searchParams.get("q")) setSearchParams({}, { replace: true }); }, []); // eslint-disable-line

  useEffect(() => { api.get("/coach/history").then((r) => { setMessages(r.data.messages); setLoaded(true); }).catch((err) => { console.warn("[Coach] request failed", err); setLoaded(true); }); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async (textArg, spoken = false) => {
    const text = (typeof textArg === "string" ? textArg : input).trim();
    if (!text || sending) return null;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/coach/message", { text, language, with_audio: voiceOn || spoken });
      if (data.audio_base64) pendingAudioRef.current = { text: data.reply, b64: data.audio_base64 };
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if ((voiceOn || spoken) && !handsFreeRef.current) playTTS(data.reply);
      return data.reply;
    } catch {
      toast.error("Linden didn't respond. Try again.");
      return null;
    } finally {
      setSending(false);
    }
  };

  const avatarState = (speaking || hfState === "speaking") ? "speaking"
    : (recording || hfState === "listening") ? "listening"
    : (sending || transcribing || hfState === "thinking") ? "thinking"
    : "idle";

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-5rem)]">
      <CoachShareDialog open={shareOpen} onClose={() => setShareOpen(false)} messageCount={messages.length} />
      <div className="flex items-center justify-between gap-3 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3 min-w-0">
          <AvatarStrip name="Linden" title="Your sales coach" avatar={COACH_AVATAR} state={avatarState} level={speaking ? level : undefined} frameKey="coach" frames={coachFrames} className="lg:hidden" />
          <div className="hidden lg:block">
            <div className="font-semibold text-slate-900">Linden</div>
            <div className="text-xs text-emerald-600 font-mono uppercase tracking-wider">Your sales coach{currentLang && currentLang.id !== "en" ? <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-900 text-white" data-testid="coach-language-badge">{currentLang.label}</span> : null}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="coach-call-controls">
          <Button onClick={() => setShareOpen(true)} variant="outline" size="sm" disabled={messages.length < 2}
            className="h-10 px-3 rounded-full font-semibold border bg-white border-slate-300 text-slate-700 hover:text-slate-900 hover:border-slate-500"
            title="Share this coaching session" data-testid="coach-share-btn">
            <Share2 className="w-5 h-5 sm:mr-1.5" /><span className="hidden sm:inline">Share</span>
          </Button>
          {languages.length > 0 && (
            <select value={language} onChange={(e) => setLanguage(e.target.value)} aria-label="Coaching language"
              className="h-10 rounded-full border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              title="Linden replies and listens in this language" data-testid="coach-language-select">
              {languages.map((l) => <option key={l.id} value={l.id}>{l.flag} {l.label}</option>)}
            </select>
          )}
          <Button onClick={() => { setVoiceOn((v) => !v); if (voiceOn) audioRef.current?.pause(); }} variant="outline" size="sm"
            className={`h-10 px-3 rounded-full font-semibold border ${voiceOn ? "bg-emerald-500/10 border-emerald-400 text-emerald-700 hover:bg-emerald-500/20" : "bg-white border-slate-300 text-slate-600 hover:text-slate-900"}`}
            title={voiceOn ? "Linden speaks replies (on)" : "Linden speaks replies (off)"} data-testid="coach-voice-toggle-btn">
            {voiceOn ? <Volume2 className="w-5 h-5 mr-1.5" /> : <VolumeX className="w-5 h-5 mr-1.5" />}
            <span>Voice {voiceOn ? "on" : "off"}</span>
          </Button>
          <Button onClick={handsFree ? endHandsFree : beginHandsFree} size="sm"
            className={`h-10 px-4 rounded-full font-semibold ${handsFree ? "bg-red-600 hover:bg-red-700 text-white" : "bg-cyan-500 hover:bg-cyan-600 text-black"}`}
            title={handsFree ? "End the call" : "Talk naturally — no buttons"} data-testid="coach-handsfree-btn">
            {handsFree ? <><PhoneOff className="w-5 h-5 mr-1.5" /> End hands-free</> : <><Radio className="w-5 h-5 mr-1.5" /> Hands-free</>}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex gap-6 min-h-0 pt-4">
        <AvatarStage name="Linden" title="Your sales coach" avatar={COACH_AVATAR} frameKey="coach" frames={coachFrames}
          state={avatarState} level={speaking ? level : undefined} className="hidden lg:flex w-72 shrink-0 self-start" />

        <div className="flex-1 flex flex-col min-h-0">
          <div ref={scrollRef} className="flex-1 overflow-y-auto pb-6 space-y-4">
            {!loaded && <div className="flex justify-center text-emerald-600"><Loader2 className="w-5 h-5 animate-spin" /></div>}
            {loaded && messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Hey {user?.name?.split(" ")[0]}, what are we working on?</h2>
                <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">Ask me anything about selling. I won't just hand you the answer — I'll make you think.</p>
                <div className="flex flex-wrap gap-2 justify-center mt-6 max-w-lg mx-auto">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="text-xs px-3 py-2 rounded-full card-surface text-slate-600 hover:border-emerald-500/40 transition-colors" data-testid="coach-suggestion">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <motion.div key={m.id || `${i}-${m.role}-${(m.content || "").slice(0, 16)}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`coach-msg-${m.role}-${i}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-emerald-500 text-black rounded-br-sm" : "card-surface text-slate-700 rounded-bl-sm"}`}>
                  {m.role === "assistant" ? <RichText text={m.content} /> : m.content}
                  {m.role === "assistant" && (
                    <button onClick={() => replay(i, m.content)} title={replaying === i ? "Stop" : "Hear Linden say this again"}
                      className={`mt-2.5 flex w-fit items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${replaying === i ? "bg-emerald-500 text-black border-emerald-500" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400 hover:text-emerald-700"}`}
                      data-testid={`coach-replay-${i}`}>
                      {replaying === i ? <><Square className="w-3 h-3" /> Stop</> : <><Play className="w-3 h-3" /> Play again</>}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="card-surface px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1">
                  <span className="typing-dot w-2 h-2 rounded-full bg-slate-500" />
                  <span className="typing-dot w-2 h-2 rounded-full bg-slate-500" style={{ animationDelay: "0.2s" }} />
                  <span className="typing-dot w-2 h-2 rounded-full bg-slate-500" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-200">
            {handsFree ? (
              <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30" data-testid="coach-handsfree-status">
                <div className="flex items-center gap-3 text-sm text-cyan-700">
                  <span className="flex gap-0.5 items-end h-5">
                    {[0, 1, 2, 3, 4].map((i) => <span key={i} className="wave-bar" style={{ height: "100%", background: "#06B6D4", animationDelay: `${i * 0.12}s`, animationPlayState: hfState === "speaking" || hfState === "listening" ? "running" : "paused" }} />)}
                  </span>
                  <span className="font-mono uppercase tracking-wider text-xs">
                    {hfState === "listening" && "Listening… just speak, pause when you're done"}
                    {hfState === "thinking" && "Linden is thinking…"}
                    {hfState === "speaking" && "Linden is speaking… just start talking to interrupt"}
                    {hfState === "idle" && "Connecting…"}
                  </span>
                </div>
                <Button onClick={endHandsFree} variant="ghost" size="sm" className="text-cyan-700 hover:text-slate-900" data-testid="coach-handsfree-stop-btn">
                  <PhoneOff className="w-4 h-4 mr-1" /> End call
                </Button>
              </div>
            ) : (
              <>
                {(recording || transcribing) && (
                  <div className="flex items-center gap-2 mb-2 text-xs font-mono text-emerald-600" data-testid="coach-voice-status">
                    {recording ? (
                      <><span className="flex gap-0.5 items-end h-4">
                        {[0, 1, 2, 3, 4].map((i) => <span key={i} className="wave-bar" style={{ height: "100%", animationDelay: `${i * 0.12}s` }} />)}
                      </span> Listening… tap the square to send</>
                    ) : <><Loader2 className="w-3 h-3 animate-spin" /> Transcribing…</>}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button onClick={recording ? stopRec : startRec} disabled={sending || transcribing} variant="outline"
                    className={`h-11 px-4 rounded-full font-semibold border ${recording ? "bg-red-600 border-red-600 text-white hover:bg-red-700 animate-pulse" : "bg-white border-slate-300 text-slate-700 hover:border-emerald-400 hover:text-emerald-700"}`}
                    title={recording ? "Tap to send what you said" : "Tap, speak, then tap again to send"} data-testid="coach-record-btn">
                    {recording ? <Square className="w-5 h-5 mr-1.5" /> : <Mic className="w-5 h-5 mr-1.5" />}
                    <span>{recording ? "Stop & send" : "Microphone"}</span>
                  </Button>
                  <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder={recording ? "Listening…" : "Ask Linden anything, or tap Microphone to speak…"} disabled={sending || recording}
                    className="flex-1 bg-slate-50 border-slate-200 text-slate-900 h-11" data-testid="coach-input" />
                  <Button onClick={() => send()} disabled={sending || !input.trim()} className="bg-emerald-500 hover:bg-emerald-600 text-black h-11 px-4 font-semibold" data-testid="coach-send-btn">
                    <Send className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Send</span>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
